/**
 * Hook for fetching and managing dashboard metrics
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { MetricsResponse, TimeRange } from '../types/dashboard';
import { fetchDashboardMetrics } from '../lib/dashboard-api';
import { fillTimeSeriesGaps } from '../lib/formatters';
import { useAuth } from '../components/Auth';

/**
 * Fill gaps in all time series data with zero values.
 * CloudWatch only returns data points for periods with activity,
 * so we need to generate zeros for missing periods.
 */
function fillAllTimeSeriesGaps(
  metrics: MetricsResponse,
  timeRange: TimeRange,
): MetricsResponse {
  return {
    ...metrics,
    timeSeries: {
      queryVolume: fillTimeSeriesGaps(
        metrics.timeSeries.queryVolume,
        timeRange,
      ),
      retrievalLatency: fillTimeSeriesGaps(
        metrics.timeSeries.retrievalLatency,
        timeRange,
      ),
      llmLatency: fillTimeSeriesGaps(metrics.timeSeries.llmLatency, timeRange),
      timeToFirstToken: fillTimeSeriesGaps(
        metrics.timeSeries.timeToFirstToken,
        timeRange,
      ),
      errors: fillTimeSeriesGaps(metrics.timeSeries.errors, timeRange),
      postFilterRejections: fillTimeSeriesGaps(
        metrics.timeSeries.postFilterRejections,
        timeRange,
      ),
      documentsIngested: fillTimeSeriesGaps(
        metrics.timeSeries.documentsIngested,
        timeRange,
      ),
      webhooksReceived: fillTimeSeriesGaps(
        metrics.timeSeries.webhooksReceived,
        timeRange,
      ),
      classifications: fillTimeSeriesGaps(
        metrics.timeSeries.classifications,
        timeRange,
      ),
      inputTokens: fillTimeSeriesGaps(
        metrics.timeSeries.inputTokens,
        timeRange,
      ),
      outputTokens: fillTimeSeriesGaps(
        metrics.timeSeries.outputTokens,
        timeRange,
      ),
    },
  };
}

// Auto-refresh intervals by time range (in milliseconds)
const REFRESH_INTERVALS: Record<TimeRange, number> = {
  '1d': 5 * 60 * 1000, // 5 minutes for daily view (more recent data)
  '3d': 10 * 60 * 1000, // 10 minutes for 3-day view
  '7d': 15 * 60 * 1000, // 15 minutes for weekly view
  '30d': 60 * 60 * 1000, // 1 hour for monthly view
};

interface UseDashboardMetricsOptions {
  autoRefresh?: boolean;
}

interface UseDashboardMetricsResult {
  data: MetricsResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  lastUpdated: Date | null;
}

/**
 * Hook to fetch dashboard metrics for a given time range
 *
 * @param timeRange - The time range to fetch metrics for (1d, 3d, 7d, 30d)
 * @param options - Optional configuration (autoRefresh: boolean)
 * @returns Object with metrics data, loading state, error, refetch function, and lastUpdated
 */
export function useDashboardMetrics(
  timeRange: TimeRange,
  options: UseDashboardMetricsOptions = {},
): UseDashboardMetricsResult {
  const { autoRefresh = true } = options;
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      setError('Authentication required');
      return;
    }

    const abortController = new AbortController();

    async function loadMetrics() {
      setIsLoading(true);
      setError(null);

      try {
        const authHeader = getAuthHeader();
        if (!authHeader) {
          throw new Error('No authentication token available');
        }

        const metrics = await fetchDashboardMetrics(
          timeRange,
          { Authorization: authHeader },
          abortController.signal,
        );
        // Fill gaps in time series with zero values for proper chart rendering
        const filledMetrics = fillAllTimeSeriesGaps(metrics, timeRange);
        setData(filledMetrics);
        setLastUpdated(new Date());
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return; // Ignore abort errors
        }
        setError(
          err instanceof Error ? err.message : 'Failed to fetch metrics',
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadMetrics();

    return () => {
      abortController.abort();
    };
  }, [timeRange, isAuthenticated, getAuthHeader, fetchTrigger]);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh || !isAuthenticated) {
      return;
    }

    const interval = REFRESH_INTERVALS[timeRange];
    intervalRef.current = setInterval(() => {
      refetch();
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, timeRange, isAuthenticated, refetch]);

  return { data, isLoading, error, refetch, lastUpdated };
}
